import React, { useMemo, useState, useEffect, useCallback } from "react";
import styled from "@emotion/styled";
import { Helmet } from "react-helmet-async";
import { DataGrid, GridToolbar } from "@mui/x-data-grid";
import AddCustomerPricingAgreement from "../../components/AddCustomerPricingAgreement";
import {
  Grid,
  Box,
  Card as MuiCard,
  Chip,
  Typography,
  Divider,
  IconButton,
  CircularProgress,
} from "@mui/material";
import { Add as AddIcon, Delete as DeleteIcon, Edit as EditIcon } from "@mui/icons-material";
import { spacing } from "@mui/system";
import Notification from "../../components/Notification";
import ConfirmDialog from "../../components/ConfirmDialog";
import axios from "axios";
import DataGridExport from "../../components/DataGridExport";
import FeeMonitorTrendPanel from "../../components/FeeMonitorTrendPanel";
import {
  feeMonitorFrequencyToDisplayLabel,
  feeMonitorFrequencyToPeriodMonths,
  feeMonitorSeverityToLabel,
} from "../../utils/feeMonitorUtils";
import { usePermissions } from "../../hooks/usePermissions";
import { CAPABILITIES } from "../../auth/permissions";

// Helper function to get user's locale decimal separator
const getDecimalSeparator = () => {
  try {
    const numberFormat = new Intl.NumberFormat(navigator.language || "en-US");
    const parts = numberFormat.formatToParts(1.5);
    const decimalPart = parts.find((part) => part.type === "decimal");
    return decimalPart ? decimalPart.value : ".";
  } catch (error) {
    return ".";
  }
};

// Helper function to format value with 2 decimals using user's locale separator
const formatWithTwoDecimals = (value) => {
  if (!value && value !== 0) {
    const separator = getDecimalSeparator();
    return `0${separator}00`;
  }
  const numValue = typeof value === "string" ? parseFloat(String(value).replace(",", ".")) : value;
  const formatted = numValue.toFixed(2);
  const separator = getDecimalSeparator();
  return separator === "," ? formatted.replace(".", ",") : formatted;
};

// Helper function to format price with euro sign
const formatPriceWithEuro = (value) => {
  return `€ ${formatWithTwoDecimals(value)}`;
};

// Helper function to parse decimal value
const parseDecimalValue = (value) => {
  if (!value && value !== 0) return 0;
  const numValue = typeof value === "string" ? parseFloat(String(value).replace(",", ".")) : value;
  return isNaN(numValue) ? 0 : numValue;
};

const Card = styled(MuiCard)(spacing);

const getProductTypeId = (productType) => {
  const numericProductType = Number(productType);
  if (numericProductType === 1 || numericProductType === 2) return numericProductType;

  const normalizedProductType = String(productType ?? "").toUpperCase();
  if (normalizedProductType === "VAT") return 1;
  if (normalizedProductType === "EXCISE") return 2;
  return null;
};

const getMonitorPeriodValue = (monitorItem) => {
  const periodMonths = feeMonitorFrequencyToPeriodMonths(monitorItem?.frequency);
  const monthlyVolume = Number(monitorItem?.latest?.volumeMonthly ?? 0);
  if (!Number.isFinite(periodMonths) || periodMonths <= 0 || !Number.isFinite(monthlyVolume)) return null;
  return monthlyVolume * periodMonths;
};

const getAgreementVolumePeriodFromMonitorItem = (monitorItem) => {
  const periodMonths = feeMonitorFrequencyToPeriodMonths(monitorItem?.frequency);
  if (periodMonths === 1) return 1;
  if (periodMonths === 3) return 2;
  if (periodMonths === 12) return 3;
  return "";
};

const CustomerPricingAgreements = ({ customerId }) => {
  const { hasPermission } = usePermissions();
  const canWritePricing = hasPermission(CAPABILITIES.customersPricing, "W");
  const [loading, setLoading] = useState(false);
  const [tableData, setTableData] = useState([]);
  const [countryData, setCountryData] = useState([]);
  const [monitorLoading, setMonitorLoading] = useState(false);
  const [monitorData, setMonitorData] = useState(null);
  const [selectedMonitorItem, setSelectedMonitorItem] = useState(null);
  const [notify, setNotify] = useState({
    isOpen: false,
    message: "",
    type: "",
  });
  const [confirmDialog, setConfirmDialog] = useState({
    isOpen: false,
    title: "",
    subTitle: "",
    onConfirm: null,
  });
  const [editDialog, setEditDialog] = useState({
    isOpen: false,
    agreement: null,
  });
  const [createDialog, setCreateDialog] = useState({
    isOpen: false,
    draft: null,
  });
  const [refundRatesCache, setRefundRatesCache] = useState({}); // Cache: key = "countryId_productType", value = array of rates

  const fetchPricingAgreements = useCallback(async () => {
    if (!customerId) return;

    setLoading(true);
    try {
      const response = await axios.get(`/api/v1/customerpricingagreements/entity/${customerId}`);
      const agreements = response.data || [];
      setTableData(agreements);
    } catch (error) {
      console.error("Error fetching pricing agreements:", error);
      setNotify({
        isOpen: true,
        message: "Error fetching pricing agreements",
        type: "error",
      });
    } finally {
      setLoading(false);
    }
  }, [customerId]);

  const fetchCountries = useCallback(async () => {
    try {
      const response = await axios.get("/api/v1/allCountries");
      setCountryData(response.data || []);
    } catch (error) {
      console.error("Error fetching countries:", error);
    }
  }, []);

  const fetchMonitor = useCallback(async () => {
    if (!customerId) return;

    setMonitorLoading(true);
    try {
      const response = await axios.get(`/api/v1/feemonitor/customer/${customerId}`, {
        // Use 48 months so "last 4 periods" works for yearly customers too.
        params: { lookbackMonths: 48 },
      });
      setMonitorData(response.data || null);
    } catch (error) {
      console.error("Error fetching fee monitor customer detail:", error);
      setMonitorData(null);
    } finally {
      setMonitorLoading(false);
    }
  }, [customerId]);

  useEffect(() => {
    fetchCountries();
  }, [fetchCountries]);

  useEffect(() => {
    if (customerId) {
      fetchPricingAgreements();
    }
  }, [customerId, fetchPricingAgreements]);

  useEffect(() => {
    fetchMonitor();
  }, [fetchMonitor]);

  const handleDelete = (id) => {
    setConfirmDialog({
      isOpen: true,
      title: "Delete Pricing Agreement",
      subTitle: "Are you sure you want to delete this pricing agreement?",
      onConfirm: async () => {
        try {
          await axios.delete(`/api/v1/customerpricingagreements/${id}`);
          setNotify({
            isOpen: true,
            message: "Pricing agreement deleted successfully",
            type: "success",
          });
          refreshPricingAgreementData();
          setConfirmDialog({ ...confirmDialog, isOpen: false });
        } catch (error) {
          console.error("Error deleting pricing agreement:", error);
          setNotify({
            isOpen: true,
            message: "Error deleting pricing agreement",
            type: "error",
          });
          setConfirmDialog({ ...confirmDialog, isOpen: false });
        }
      },
    });
  };

  const handleEdit = (agreement) => {
    setEditDialog({
      isOpen: true,
      agreement: agreement,
    });
  };

  const getCountryName = (countryId) => {
    const country = countryData.find((c) => c.countryId === countryId);
    return country ? `${country.countryCode} - ${country.countryName}` : "";
  };

  const getProductName = (productType) => {
    const productTypeId = getProductTypeId(productType);
    return productTypeId === 1 ? "VAT" : productTypeId === 2 ? "Excise" : "";
  };

  // Fetch refund rates for a specific country and product type
  const fetchRefundRates = useCallback(async (countryId, productType) => {
    if (!countryId || !productType) return [];
    const cacheKey = `${countryId}_${productType}`;
    
    // Return cached if available
    if (refundRatesCache[cacheKey]) {
      return refundRatesCache[cacheKey];
    }

    try {
      const response = await axios.get(`/api/v1/refundrates/country/${countryId}`);
      const fetchedRates = response.data || [];
      const filteredRates = fetchedRates.filter((rate) => rate.productId === productType);
      
      // Sort by fromRate ascending
      const sortedRates = [...filteredRates].sort((a, b) => {
        const fromRateA = parseDecimalValue(a.fromRate);
        const fromRateB = parseDecimalValue(b.fromRate);
        return fromRateA - fromRateB;
      });

      // Cache the rates
      setRefundRatesCache((prev) => ({
        ...prev,
        [cacheKey]: sortedRates,
      }));

      return sortedRates;
    } catch (error) {
      console.error(`Error fetching refund rates for country ${countryId}, product ${productType}:`, error);
      return [];
    }
  }, [refundRatesCache]);

  // Find rate range (fromRate - toRate) based on the rate value
  const getRateRange = useCallback((rate, countryId, productType) => {
    if (!rate && rate !== 0) return null;
    
    const rateValue = parseDecimalValue(rate);
    const cacheKey = `${countryId}_${productType}`;
    const rates = refundRatesCache[cacheKey] || [];
    
    // Find the rate that matches (rate should be between fromRate and toRate, or equal to fromRate)
    const matchingRate = rates.find((r) => {
      const fromRate = parseDecimalValue(r.fromRate);
      const toRate = parseDecimalValue(r.toRate);
      return rateValue >= fromRate && rateValue <= toRate;
    });

    if (matchingRate) {
      return {
        fromRate: matchingRate.fromRate,
        toRate: matchingRate.toRate,
      };
    }

    return null;
  }, [refundRatesCache]);

  // Fetch refund rates for all unique country/product combinations when tableData changes
  useEffect(() => {
    const fetchAllRefundRates = async () => {
      if (!tableData || tableData.length === 0) return;

      // Get unique country/product combinations
      const uniqueCombos = new Set();
      tableData.forEach((agreement) => {
        if (agreement.country && agreement.productType) {
          uniqueCombos.add(`${agreement.country}_${agreement.productType}`);
        }
      });

      // Fetch rates for each unique combination
      for (const combo of uniqueCombos) {
        const [countryId, productType] = combo.split("_");
        await fetchRefundRates(parseInt(countryId), parseInt(productType));
      }
    };

    fetchAllRefundRates();
  }, [tableData, fetchRefundRates]);

  const monitorIndex = useMemo(() => {
    const items = monitorData?.items;
    if (!Array.isArray(items)) return new Map();

    const map = new Map();
    items.forEach((it) => {
      const productName = getProductName(it?.productType);
      if (!it?.countryId || !productName) return;
      const key = `${it.countryId}|${productName}`;
      map.set(key, it);
    });
    return map;
  }, [monitorData]);

  const combinedTableData = useMemo(() => {
    const agreements = Array.isArray(tableData) ? tableData : [];
    const monitorItems = Array.isArray(monitorData?.items) ? monitorData.items : [];
    const agreementKeys = new Set();

    const agreementRows = agreements.map((agreement) => {
      const key = `${agreement.country}|${getProductName(agreement.productType)}`;
      agreementKeys.add(key);
      return {
        ...agreement,
        rowKey: `agreement-${agreement.id}`,
        hasPricingAgreement: true,
        isMissingPricingAgreement: false,
        monitorItem: monitorIndex.get(key) || null,
      };
    });

    const missingAgreementRows = monitorItems.reduce((rows, monitorItem) => {
      const productTypeId = getProductTypeId(monitorItem?.productType);
      const productName = getProductName(productTypeId);
      if (!monitorItem?.countryId || !productTypeId || !productName) return rows;

      const key = `${monitorItem.countryId}|${productName}`;
      if (agreementKeys.has(key) || monitorItem?.hasPricingAgreement) return rows;

      const monitorVolume = getMonitorPeriodValue(monitorItem);
      rows.push({
        id: null,
        rowKey: `missing-${monitorItem.countryId}-${productTypeId}`,
        country: monitorItem.countryId,
        productType: productTypeId,
        volumePeriod: getAgreementVolumePeriodFromMonitorItem(monitorItem),
        volume: Number.isFinite(monitorVolume) ? monitorVolume : null,
        rate: null,
        tieredPrice: null,
        priceAdjustmentPercent: null,
        hasPricingAgreement: false,
        isMissingPricingAgreement: true,
        monitorItem,
      });
      return rows;
    }, []);

    return [...agreementRows, ...missingAgreementRows];
  }, [monitorData, monitorIndex, tableData]);

  const missingAgreementCount = useMemo(
    () => combinedTableData.filter((row) => row?.isMissingPricingAgreement).length,
    [combinedTableData]
  );

  const fallbackTierLinesForSelectedMonitorItem = useMemo(() => {
    if (!selectedMonitorItem) return [];
    const items = monitorData?.items;
    if (!Array.isArray(items) || items.length === 0) return [];

    const match = items.find(
      (it) =>
        it &&
        getProductTypeId(it.productType) === getProductTypeId(selectedMonitorItem.productType) &&
        Array.isArray(it.tierLines) &&
        it.tierLines.length > 0
    );

    return match?.tierLines || [];
  }, [monitorData, selectedMonitorItem]);

  const getMonitorItemForRow = useCallback(
    (row) => {
      if (row?.monitorItem) return row.monitorItem;
      const productName = getProductName(row?.productType);
      if (!row?.country || !productName) return null;
      return monitorIndex.get(`${row.country}|${productName}`) || null;
    },
    [monitorIndex]
  );

  useEffect(() => {
    if (!selectedMonitorItem) return;

    const productName = getProductName(selectedMonitorItem.productType);
    const refreshedMonitorItem = monitorIndex.get(`${selectedMonitorItem.countryId}|${productName}`) || null;
    if (refreshedMonitorItem) {
      setSelectedMonitorItem(refreshedMonitorItem);
    }
  }, [monitorIndex, selectedMonitorItem]);

  const handleCreateFromMonitor = useCallback(
    (row) => {
      const monitorItem = getMonitorItemForRow(row);
      if (!monitorItem) return;

      const agreementVolumePeriod = getAgreementVolumePeriodFromMonitorItem(monitorItem);
      const monitorVolume = getMonitorPeriodValue(monitorItem);
      setEditDialog({ isOpen: false, agreement: null });
      setCreateDialog({
        isOpen: true,
        draft: {
          productType: getProductTypeId(row?.productType),
          country: row?.country,
          volumePeriod: agreementVolumePeriod,
          volume: agreementVolumePeriod && Number.isFinite(monitorVolume) ? monitorVolume : "",
        },
      });

      if (!agreementVolumePeriod) {
        setNotify({
          isOpen: true,
          message: "Pricing agreement period could not be prefilled. Please choose the volume period manually.",
          type: "info",
        });
      }
    },
    [getMonitorItemForRow]
  );

  const refreshPricingAgreementData = useCallback(async () => {
    await fetchPricingAgreements();
    await fetchMonitor();
  }, [fetchMonitor, fetchPricingAgreements]);

  const severityDotColor = (severity) => {
    switch (feeMonitorSeverityToLabel(severity)) {
      case "RED":
        return "error.main";
      case "AMBER":
        return "warning.main";
      case "INFO":
        return "info.main";
      default:
        return "grey.500";
    }
  };

  const getVolumePeriodName = (volumePeriod) => {
    switch (volumePeriod) {
      case 1:
        return "Month";
      case 2:
        return "Quarter";
      case 3:
        return "Year";
      default:
        return "";
    }
  };

  const getPreFinancePeriodLabel = (periodType, productType) => {
    if (!periodType) return "-";
    if (periodType === 1) return "p/M";
    if (periodType === 2) return "p/Q";
    return "";
  };

  const calculatePrice = (tieredPrice, priceAdjustmentPercent) => {
    const tieredPriceValue = parseFloat(String(tieredPrice).replace(",", ".")) || 0;
    const adjustmentPercent = parseFloat(String(priceAdjustmentPercent).replace(",", ".")) || 0;

    // Calculation: Tier Price × (1 + Adjustment%)
    const calculated = tieredPriceValue * (1 + adjustmentPercent / 100);
    return calculated;
  };

  const getAdjustmentLabel = (priceAdjustmentPercent) => {
    const adjustmentValue = parseFloat(String(priceAdjustmentPercent).replace(",", ".")) || 0;
    if (adjustmentValue === 0) {
      return `${formatWithTwoDecimals(0)}%`;
    }
    const adjustmentType = adjustmentValue < 0 ? "Discount" : "Surcharge";
    const sign = adjustmentValue > 0 ? "+" : "";
    return `${sign}${formatWithTwoDecimals(Math.abs(adjustmentValue))}% (${adjustmentType})`;
  };

  const convertTierPriceToMonthly = (tieredPrice, volumePeriod) => {
    const tieredPriceValue = parseFloat(String(tieredPrice).replace(",", ".")) || 0;
    
    // Convert to monthly based on volume period
    // 1 = Month, 2 = Quarter, 3 = Year
    switch (volumePeriod) {
      case 1: // Month - no conversion needed
        return tieredPriceValue;
      case 2: // Quarter - divide by 3
        return tieredPriceValue / 3;
      case 3: // Year - divide by 12
        return tieredPriceValue / 12;
      default:
        return tieredPriceValue;
    }
  };

  const columns = [
    {
      field: "monitor",
      headerName: "Monitor",
      width: 90,
      sortable: false,
      filterable: false,
      renderCell: (params) => {
        if (getProductTypeId(params.row.productType) !== 1) {
          return <Typography variant="body2" color="text.secondary">-</Typography>;
        }

        const item = getMonitorItemForRow(params.row);
        if (!item) return <Typography variant="body2" color="text.secondary">-</Typography>;

        const hasPricingAgreement = params.row.hasPricingAgreement !== false && item?.hasPricingAgreement !== false;
        const dotColor = hasPricingAgreement ? severityDotColor(item.pricingSeverity) : "error.main";
        const tooltipLabel = hasPricingAgreement ? feeMonitorSeverityToLabel(item.pricingSeverity) : "No pricing agreement";

        return (
          <Box
            role="button"
            tabIndex={0}
            aria-label="Open fee monitor details"
            onClick={() => setSelectedMonitorItem(item)}
            onKeyDown={(e) => {
              if (e.key === "Enter" || e.key === " ") setSelectedMonitorItem(item);
            }}
            title={tooltipLabel}
            sx={{
              width: 14,
              height: 14,
              borderRadius: "50%",
              bgcolor: dotColor,
              cursor: "pointer",
              boxShadow: 1,
              outline: "none",
              "&:focus-visible": { boxShadow: 3 },
            }}
          />
        );
      },
    },
    { field: "id", headerName: "ID", width: 70, hide: true },
    {
      field: "productType",
      headerName: "Product",
      flex: 0.5,
      valueGetter: (params) => getProductName(params.row.productType),
    },
    {
      field: "country",
      headerName: "Country (MSR)",
      flex: 1.2,
      valueGetter: (params) => getCountryName(params.row.country),
    },
    {
      field: "volumePeriod",
      headerName: "Volume Period",
      flex: 0.7,
      valueGetter: (params) => {
        if (params.row.isMissingPricingAgreement) {
          return feeMonitorFrequencyToDisplayLabel(params.row?.monitorItem?.frequency?.label ?? params.row?.monitorItem?.frequency);
        }
        return getVolumePeriodName(params.row.volumePeriod);
      },
    },
    {
      field: "volume",
      headerName: "Volume",
      flex: 0.5,
      valueGetter: (params) => {
        if (params.row.isMissingPricingAgreement) {
          const monitorVolume = getMonitorPeriodValue(params.row.monitorItem);
          return Number.isFinite(monitorVolume) ? formatWithTwoDecimals(monitorVolume) : "-";
        }
        return formatWithTwoDecimals(params.row.volume);
      },
    },
    {
      field: "rate",
      headerName: "Rate (Monthly)",
      flex: 0.8,
      valueGetter: (params) => {
        if (params.row.isMissingPricingAgreement) return "No pricing agreement";
        const rateRange = getRateRange(params.row.rate, params.row.country, params.row.productType);
        if (rateRange) {
          return `${formatWithTwoDecimals(rateRange.fromRate)} - ${formatWithTwoDecimals(rateRange.toRate)}`;
        }
        // Fallback to just the rate value if no range found
        return formatWithTwoDecimals(params.row.rate);
      },
    },
    {
      field: "tieredPrice",
      headerName: "Tier Price (Monthly)",
      flex: 1,
      valueGetter: (params) => {
        if (params.row.isMissingPricingAgreement) return "-";
        const monthlyPrice = convertTierPriceToMonthly(params.row.tieredPrice, params.row.volumePeriod);
        return formatPriceWithEuro(monthlyPrice);
      },
    },
    {
      field: "priceAdjustmentPercent",
      headerName: "Adjustment (%)",
      flex: 0.8,
      valueGetter: (params) => {
        if (params.row.isMissingPricingAgreement) return "-";
        return getAdjustmentLabel(params.row.priceAdjustmentPercent ?? params.row.discount ?? 0);
      },
    },
    {
      field: "vf",
      headerName: "VF (%)",
      flex: 0.6,
      valueGetter: (params) => {
        if (params.row.isMissingPricingAgreement) return "-";
        if (!params.row.vf && params.row.vf !== 0) return "-";
        return `${formatWithTwoDecimals(params.row.vf)}%`;
      },
    },
    {
      field: "preFinancePeriodType",
      headerName: "Pre-finance",
      flex: 0.7,
      valueGetter: (params) => {
        if (params.row.isMissingPricingAgreement) return "-";
        if (!params.row.vf && params.row.vf !== 0) return "-";
        return getPreFinancePeriodLabel(params.row.preFinancePeriodType, params.row.productType);
      },
    },
    {
      field: "calculatedPrice",
      headerName: "Price (Monthly)",
      flex: 1,
      valueGetter: (params) => {
        if (params.row.isMissingPricingAgreement) return "-";
        // Convert tier price to monthly first
        const monthlyTierPrice = convertTierPriceToMonthly(params.row.tieredPrice, params.row.volumePeriod);
        const calculatedMonthly = calculatePrice(
          monthlyTierPrice,
          params.row.priceAdjustmentPercent ?? params.row.discount ?? 0
        );
        // Calculate yearly price (monthly × 12)
        const calculatedYearly = calculatedMonthly * 12;
        return `${formatPriceWithEuro(calculatedMonthly)} (${formatPriceWithEuro(calculatedYearly)} p.a.)`;
      },
    },
    {
      field: "netDifference",
      headerName: "Net Difference",
      flex: 1,
      cellClassName: (params) => {
        if (params.row.isMissingPricingAgreement) return "net-difference-error";
        const monitorItem = getMonitorItemForRow(params.row);
        if (!monitorItem?.latest) return "net-difference-neutral";
        const deltaMonthlyBase = Number(monitorItem.latest.deltaFeeMonthly ?? 0) || 0;
        const catchups = Array.isArray(monitorItem?.yearCatchups) ? monitorItem.yearCatchups : [];
        const latestCatchup = [...catchups].sort((a, b) => Number(b?.year ?? 0) - Number(a?.year ?? 0))[0];
        const deltaMonthly = latestCatchup ? Number(latestCatchup?.deltaFeeMonthly ?? 0) || 0 : deltaMonthlyBase;
        if (deltaMonthly < 0) return "net-difference-error";
        if (deltaMonthly > 0) return "net-difference-warning";
        return "net-difference-success";
      },
      valueGetter: (params) => {
        if (params.row.isMissingPricingAgreement) {
          return "No pricing agreement";
        }
        const monitorItem = getMonitorItemForRow(params.row);
        if (!monitorItem?.latest) {
          return "-";
        }
        const deltaMonthlyBase = Number(monitorItem.latest.deltaFeeMonthly ?? 0) || 0;
        const catchups = Array.isArray(monitorItem?.yearCatchups) ? monitorItem.yearCatchups : [];
        const latestCatchup = [...catchups].sort((a, b) => Number(b?.year ?? 0) - Number(a?.year ?? 0))[0];
        const deltaMonthly = latestCatchup ? Number(latestCatchup?.deltaFeeMonthly ?? 0) || 0 : deltaMonthlyBase;
        const deltaYearly = deltaMonthly * 12;
        
        // Positive = customer pays too much, negative = customer pays too little.
        const sign = deltaMonthly > 0 ? "+" : "";
        return `${sign}${formatPriceWithEuro(deltaMonthly)} (${sign}${formatPriceWithEuro(deltaYearly)} p.a.)`;
      },
      renderCell: (params) => {
        if (params.row.isMissingPricingAgreement) {
          return (
            <Typography variant="body2" sx={{ color: "error.main", fontWeight: 600 }}>
              No pricing agreement
            </Typography>
          );
        }
        const monitorItem = getMonitorItemForRow(params.row);
        if (!monitorItem?.latest) {
          return <Typography variant="body2" color="text.secondary">-</Typography>;
        }
        const deltaMonthlyBase = Number(monitorItem.latest.deltaFeeMonthly ?? 0) || 0;
        const catchups = Array.isArray(monitorItem?.yearCatchups) ? monitorItem.yearCatchups : [];
        const latestCatchup = [...catchups].sort((a, b) => Number(b?.year ?? 0) - Number(a?.year ?? 0))[0];
        const deltaMonthly = latestCatchup ? Number(latestCatchup?.deltaFeeMonthly ?? 0) || 0 : deltaMonthlyBase;
        const deltaYearly = deltaMonthly * 12;
        const sign = deltaMonthly > 0 ? "+" : "";
        const color =
          deltaMonthly < 0
            ? "#d32f2f" // Customer pays too little
            : deltaMonthly > 0
            ? "#ed6c02" // Customer pays too much
            : "#2e7d32"; // Customer pays exactly the expected amount
        return (
          <Typography variant="body2" sx={{ color: `${color} !important`, fontWeight: 700 }}>
            {`${sign}${formatPriceWithEuro(deltaMonthly)} (${sign}${formatPriceWithEuro(deltaYearly)} p.a.)`}
          </Typography>
        );
      },
    },
    {
      field: "actions",
      headerName: "Actions",
      width: 90,
      sortable: false,
      renderCell: (params) => {
        if (!canWritePricing) {
          return null;
        }
        if (params.row.isMissingPricingAgreement) {
          return (
            <Box>
              <IconButton
                onClick={() => handleCreateFromMonitor(params.row)}
                size="small"
                title="Create pricing agreement"
                color="primary"
              >
                <AddIcon />
              </IconButton>
            </Box>
          );
        }

        return (
          <Box>
            <IconButton
              onClick={() => handleEdit(params.row)}
              size="small"
              title="Edit pricing agreement"
            >
              <EditIcon />
            </IconButton>
            <IconButton
              onClick={() => handleDelete(params.row.id)}
              size="small"
              title="Delete pricing agreement"
              color="error"
            >
              <DeleteIcon />
            </IconButton>
          </Box>
        );
      },
    },
  ];

  return (
    <React.Fragment>
      <Helmet title="Pricing Agreements" />
      <Divider my={6} />
      <Card mb={0}>
        <Box sx={{ padding: 2 }}>
          <Typography variant="h6" gutterBottom>
            Pricing Agreements
          </Typography>
          <Typography variant="body2" gutterBottom>
            Overview of pricing agreements for this customer per Product and Country.
          </Typography>
          {missingAgreementCount > 0 ? (
            <Box sx={{ mt: 1 }}>
              <Chip
                color="error"
                variant="outlined"
                size="small"
                label={`${missingAgreementCount} ${missingAgreementCount === 1 ? "country" : "countries"} with fee monitor data but no pricing agreement`}
              />
            </Box>
          ) : null}
        </Box>
        <Grid container spacing={0} mt={0}>
          <AddCustomerPricingAgreement
            customerId={customerId}
            refreshTable={refreshPricingAgreementData}
            countryData={countryData}
            setNotify={setNotify}
            editDialog={editDialog}
            setEditDialog={setEditDialog}
            createDialog={createDialog}
            setCreateDialog={setCreateDialog}
          />
          <Notification notify={notify} setNotify={setNotify} />
          <ConfirmDialog confirmDialog={confirmDialog} setConfirmDialog={setConfirmDialog} />
          <Grid item xs={12}>
            {loading ? (
              <Box display="flex" justifyContent="center" my={4}>
                <CircularProgress />
              </Box>
            ) : (
              <Grid container spacing={2} sx={{ px: 2, pb: 2 }}>
                <Grid item xs={12} md={selectedMonitorItem ? 7 : 12}>
                  <Box sx={{ display: "flex", flexDirection: "column", alignItems: "flex-end", gap: 2, mb: 2 }}>
                    <Box sx={{ display: "flex", gap: 1 }}>
                      <DataGridExport tableData={combinedTableData} columns={columns} />
                    </Box>
                  </Box>
                  <DataGrid
                    getRowId={(row) => row.rowKey}
                    getRowClassName={(params) =>
                      params.row.isMissingPricingAgreement ? "missing-pricing-agreement-row" : ""
                    }
                    disableSelectionOnClick
                    components={{ Toolbar: GridToolbar }}
                    density="compact"
                    componentsProps={{
                      toolbar: {
                        csvOptions: { disableToolbarButton: false },
                        printOptions: { disableToolbarButton: false },
                        showQuickFilter: true,
                        quickFilterProps: { debounceMs: 250 },
                      },
                      rowsContainer: {
                        onClick: (e) => {
                          if (e.target.closest("button") || e.target.closest("a")) {
                            e.stopPropagation();
                          }
                        },
                      },
                    }}
                    rows={combinedTableData}
                    columns={columns}
                    pageSize={15}
                    sx={{
                      height: 667,
                      width: "100%",
                      borderRadius: 1,
                      backgroundColor: "background.paper",
                      boxShadow: 2,
                      "& .MuiDataGrid-cell:hover": {
                        color: "primary.main",
                      },
                      "& .missing-pricing-agreement-row": {
                        backgroundColor: "rgba(211, 47, 47, 0.10)",
                      },
                      "& .missing-pricing-agreement-row:hover": {
                        backgroundColor: "rgba(211, 47, 47, 0.16)",
                      },
                      "& .missing-pricing-agreement-row .MuiDataGrid-cell": {
                        borderColor: "rgba(211, 47, 47, 0.18)",
                      },
                      "& .MuiDataGrid-cell.net-difference-error": {
                        color: "error.main !important",
                        fontWeight: 600,
                      },
                      "& .MuiDataGrid-cell.net-difference-warning": {
                        color: "warning.main !important",
                        fontWeight: 600,
                      },
                      "& .MuiDataGrid-cell.net-difference-success": {
                        color: "success.main !important",
                        fontWeight: 600,
                      },
                      "& .MuiDataGrid-cell.net-difference-neutral": {
                        color: "text.secondary !important",
                      },
                      marginTop: 0,
                    }}
                  />
                  {monitorLoading ? (
                    <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 1 }}>
                      Loading fee monitor details...
                    </Typography>
                  ) : null}
                </Grid>

                {selectedMonitorItem ? (
                  <Grid item xs={12} md={5}>
                    <FeeMonitorTrendPanel
                      item={selectedMonitorItem}
                      fallbackTierLines={fallbackTierLinesForSelectedMonitorItem}
                      onClose={() => setSelectedMonitorItem(null)}
                    />
                  </Grid>
                ) : null}
              </Grid>
            )}
          </Grid>
        </Grid>
      </Card>
    </React.Fragment>
  );
};

export default CustomerPricingAgreements;

